<script setup>
import { ref, watch } from 'vue';

// Props
const props = defineProps({
    visible: { type: Boolean, default: false },
    dialogHeader: { type: String, default: '' },
    dialogMessage: { type: String, default: '' },
    dialogIcon: { type: String, default: '' },
    dialogWidth: { type: String, default: '20.5rem' },
    dialogHeight: { type: String, default: '11rem' },
    dialogButtonSeverity: { type: String, default: '' },
    dialogColor: { type: String, default: '#0038EA' }
});

const emit = defineEmits(['confirm', 'cancel', 'update:visible']);

const displayDialog = ref(props.visible);

watch(
    () => props.visible,
    (newValue) => {
        displayDialog.value = newValue;
    }
);

watch(displayDialog, (newValue) => {
    emit('update:visible', newValue);
});

const closeDialog = () => {
    displayDialog.value = false;
    emit('cancel');
};

const confirmDialog = () => {
    //console.log('Dialog confirmed!');
    emit('confirm');
    closeDialog();
};
</script>

<template>
    <div v-if="displayDialog" class="modal-overlay">
        <div class="modal-background"></div>
        <div class="modal-container" :style="{ width: props.dialogWidth, height: props.dialogHeight }">
            <div class="modal-content">
                <div class="icon-container" :style="{ backgroundColor: props.dialogColor }">
                    <svg class="icon" stroke="white" fill="none" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                </div>
                <div class="text-content">
                    <h3 class="modal-header">{{ dialogHeader }}</h3>
                    <p class="modal-message">{{ dialogMessage }}</p>
                </div>
            </div>
            <div class="button-container">
                <button @click="closeDialog" class="cancel-button">취소</button>
                <button @click="confirmDialog" class="confirm-button" :style="{ backgroundColor: props.dialogColor }">확인</button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.modal-overlay {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
    overflow-y: auto;
}

.modal-background {
    position: fixed;
    inset: 0;
    background-color: rgba(31, 41, 55, 0.35);
    transition: opacity 0.3s ease-out;
}

.modal-container {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: white;
    border-radius: 0.5rem;
    box-shadow:
        0 20px 25px -5px rgba(0, 0, 0, 0.1),
        0 10px 10px -5px rgba(0, 0, 0, 0.04);
    transition: all 0.3s ease-out;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    max-width: 24rem;
}

.modal-content {
    display: flex;
    align-items: flex-start;
    padding: 1rem;
}

.icon-container {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 9999px;
    opacity: 0.45;
}

.icon {
    width: 1.25rem;
    height: 1.25rem;
}

.text-content {
    margin-left: 1rem;
    flex-grow: 1;
}

.modal-header {
    font-size: 1.125rem;
    font-weight: 500;
    color: #111827;
}

.modal-message {
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: #4b5563;
}

.button-container {
    display: flex;
    justify-content: space-between;
    padding: 0.75rem 1.5rem;
}

.cancel-button,
.confirm-button {
    padding: 0.5rem 1rem;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    font-family: 'ScoreDream400';
    width: 8.3rem;
    transition: all 0.2s;
}

.cancel-button {
    background-color: white;
    color: #374151;
    border: 0.1rem solid #d1d5db;
}

.cancel-button:hover {
    background-color: #f3f4f6;
}

.confirm-button {
    color: white;
}

.confirm-button:hover {
    opacity: 0.8;
}

@media (max-width: 600px) {
    .modal-container {
        max-width: 23rem;
    }

    .cancel-button,
    .confirm-button {
        width: 8.5rem;
    }
}
</style>
